import React, { useState } from "react";

import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import Home from "./components/home/Home.jsx";
import Founders from "./components/about/founders/Founders.jsx";
import Spirituality from "./components/about/spirituality/Spirituality.jsx";
import TrendingList from "./components/trending/TrendingList.jsx";
import ArticleList from "./components/articles/ArticleList.jsx";
import ArticleDetail from "./components/articles/ArticleDetail.jsx";
import FounderDetail from "./components/about/founders/FounderDetail.jsx";
import PaulineMission from "./components/about/mission/PaulineMission.jsx";
import ReadingList from "./components/readings/ReadingList.jsx";
import Gallery from "./components/gallery/Gallery.jsx";
import FooterSection from "./components/utilities/Footer.jsx";
import History from "./components/about/history/History.jsx";
import Header from "./components/utilities/Header.jsx";
import { Skeleton } from "antd";
import {
  Cooperators,
  CooperatorsMission,
  CooperatorsSpirituality,
} from "./components/cooperators";
import axiosInstance from "./components/utilities/axiosInstance";
import NotFoundPage from "./components/error/NotFoundPage.jsx";
import resources from "./assets/resources.svg";
import gallery from "./assets/gallery.svg";
import trending from "./assets/trending.svg";
import catalogue from "./assets/catalogue.svg";

function App() {
  const [banners, setBanners] = useState(null);
  const [icons, setIcons] = useState([]);
  const [articles, setArticles] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [footerImage, setFooterImage] = useState(null);
  const pages = {
    home: "Home Page",
    founders: "Who We Are - Founders",
    history: "Who We Are - Our History",
    paulineSpirituality: "Who We Are - Our Spirituality",
    paulineMission: "Who We Are - Pauline Mission",
    cooperatorsMission: "Cooperators Mission",
    cooperatorsSpirituality: "Cooperators Spirituality",
    cooperators: "Cooperators",
    mission: "Mission",
    spirituality: "Spirituality",
  };

  const getCurrentPage = React.useCallback(() => {
    const pathName = window.location.pathname;
    let currentPagePath = pathName.split("/").pop().toLowerCase();

    if (!currentPagePath) {
      return pages.home;
    }

    switch (currentPagePath) {
      case "founders":
        return pages.founders;
      case "history":
        return pages.history;
      case "spirituality":
        return pages.spirituality;
      case "mission":
        return pages.mission;
      case "cooperators":
        return pages.cooperators;
      case "cooperators-mission":
        return pages.cooperatorsMission;
      case "cooperators-spirituality":
        return pages.cooperatorsSpirituality;
      default:
        return pages.home;
    }
  }, [
    pages.cooperators,
    pages.cooperatorsMission,
    pages.cooperatorsSpirituality,
    pages.founders,
    pages.history,
    pages.home,
    pages.mission,
    pages.spirituality,
  ]);

  React.useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await axiosInstance.get("sitemanagement/banners/");

        const bannersByPage = {};

        response.data.forEach((banner) => {
          const pageName = banner.page.name.trim().toLowerCase();

          if (!(pageName in bannersByPage)) {
            bannersByPage[pageName] = [];
          }
          if (banner.name.trim().toLowerCase() === 'footer') {
            setFooterImage(banner.banner_image);
          } else {
            bannersByPage[pageName].push(banner);
          }
        });

        setBanners(bannersByPage);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    // Content for the what's trending, catalogue, e.t.c section
    const fetchIcons = async () => {
      try {
        const response = await axiosInstance.get("sitemanagement/content/");
        const content = response.data;
        if (Array.isArray(content) && content.length > 0) {
          content.forEach((element) => {
            let presentIcons = icons
            let oneContent = {
              icon: element.banner_image ? element.banner_image : '',
              href: element.redirect_url,
              animation: "fade-left",
              link: element.redirect_url,
            }
            presentIcons.push(oneContent)
            setIcons(presentIcons)
          });
        } else {
          setIcons([
            {
              icon: trending,
              href: " https://e.paulinesafrica.org/index.php/book/bookDetails/1687",
              animation: "fade-left",
              link: "/trending",
            },
            {
              icon: gallery,
              href: "https://e.paulinesafrica.org/index.php/book/bookDetails/1686",
              animation: "fade-left",
              link: "/gallery/past",
            },

            {
              icon: resources,
              href: "https://e.paulinesafrica.org/index.php/book/bookDetails/1686",
              animation: "fade-right",
              link: "/readings",
            },
            {
              icon: catalogue,
              href: "https://e.paulinesafrica.org/index.php/book/bookDetails/1686",
              animation: "fade-right",
              link: "https://shop.paulinesafrica.org",
            },
          ])
        }

      } catch (error) {
        setError(error.message);
      }
    };

    const fetchArticles = async () => {
      try {
        const response = await axiosInstance.get("articles/");
        setArticles(response.data)
      } catch (error) {
        setError(error.message);
      }
    };

    const handlePopstate = () => {
      const currentPage = getCurrentPage();
      setCurrentPage(currentPage);
    };

    fetchBanners();
    fetchIcons();
    fetchArticles();
    // listen to page changes and update the state accordingly
    window.addEventListener("popstate", handlePopstate);

    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, [currentPage, getCurrentPage]);

  // Function to dynamically import a component and extract the default export
  const importComponent = (path) => {
    return React.lazy(() =>
      import(path).then((module) => ({ default: module }))
    );
  };
  return (
    <Router>
      <div className="App">
        <Header />
        <Routes>
          <Route
            path="/"
            element={
              <Home banners={banners} icons={icons} isLoading={isLoading} error={error} />
            }
          />
          <Route
            path="/founders"
            element={
              <React.Suspense fallback={<Skeleton active />}>
                <Founders />
              </React.Suspense>
            }
          />
          <Route
            path="/founders/:founderID"
            element={
              <React.Suspense fallback={<Skeleton active />}>
                <FounderDetail />
              </React.Suspense>
            }
          />
          <Route
            path="/spirituality"
            element={
              <React.Suspense fallback={<Skeleton active />}>
                <Spirituality />
              </React.Suspense>
            }
          />
          <Route
            path="/history"
            element={
              <History banners={banners} isLoading={isLoading} error={error} />
            }
          />
          <Route
            path="/mission"
            element={
              <PaulineMission
                banners={banners}
                isLoading={isLoading}
                error={error}
              />
            }
          />
          <Route
            path="/cooperators-mission"
            element={
              <CooperatorsMission
                banners={banners}
                isLoading={isLoading}
                error={error}
              />
            }
          />
          <Route
            path="/cooperators-spirituality"
            element={
              <CooperatorsSpirituality
                banners={banners}
                isLoading={isLoading}
                error={error}
              />
            }
          />
          <Route
            path="/cooperators"
            element={
              <Cooperators
                banners={banners}
                isLoading={isLoading}
                error={error}
              />
            }
          />
          <Route
            path="/readings/"
            element={
              <ReadingList
                banners={banners}
                isLoading={isLoading}
                error={error}
              />
            }
          />
          <Route
            path="/gallery/:type"
            element={<Gallery />}
            banners={banners}
            isLoading={isLoading}
            error={error}
          />
          <Route
            path="/trending"
            element={
              <TrendingList
                banners={banners}
                isLoading={isLoading}
                error={error}
              />
            }
          />
          <Route path="/articles" element={<ArticleList />} />
          <Route path="/article/:article_id" element={<ArticleDetail articles={articles} />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <FooterSection footerImage={footerImage} />
      </div>
    </Router>
  );
}

export default App;
